import React, { useState } from 'react';
import { ImageDecorator } from 'react-viewer/lib/ViewerProps';
import { useRecoilState } from 'recoil';
import { Constants } from 'src/constants';
import { useDialog } from 'src/container/DialogContext';
import { useStudentActivitySessionSaveOne } from 'src/generated/endpoint';
import { ActivitySession, StudentActivitySession, UploadFileTypeEnum } from 'src/generated/model';
import { useFileUpload } from 'src/hooks/useFileUpload';
import { fileType, useImageAndDocument } from 'src/hooks/useImageAndDocument';
import { toastState } from 'src/store';
import { getSessionTimeInfo } from 'src/util/activityv3';
import { checkFileSizeLimit100MB, isPdfFile } from 'src/util/file';
import { DocumentObjectComponentDel } from '../DocumentObjectComponentDel';
import { ButtonV2 } from '../common/ButtonV2';
import { TextareaV2 } from '../common/TextareaV2';
import { Typography } from '../common/Typography';
import { ImageCard } from '../ib/ImageCard';
import SVGIcon from '../icon/SVGIcon';
import SolidSVGIcon from '../icon/SolidSVGIcon';
import { SuperSurveyComponent } from '../survey/SuperSurveyComponent';

interface StudentActivitySessionSubmitViewProps {
  activitySession: ActivitySession;
  studentActivitySession?: StudentActivitySession;
  refetch: () => void;
  readOnly?: boolean;
}

export const StudentActivitySessionSubmitView: React.FC<StudentActivitySessionSubmitViewProps> = ({
  activitySession: activity,
  studentActivitySession: studentActivity,
  readOnly = false,
  refetch,
}) => {
  const { confirm } = useDialog();
  const [, setToastMsg] = useRecoilState(toastState);
  const { mutate: saveStudentActivitySession } = useStudentActivitySessionSaveOne({
    mutation: {
      onSuccess: () => {
        setLoading(false);
        refetch();
      },
      onError: (error) => {
        setLoading(false);
        setToastMsg(error.message);
      },
    },
  });

  const [isLoading, setLoading] = useState(false);

  const [content, setContent] = useState(studentActivity?.content || '');

  const {
    imageObjectMap,
    documentObjectMap,
    handleImageAdd,
    toggleImageDelete,
    handleDocumentAdd,
    toggleDocumentDelete,
    addFiles,
  } = useImageAndDocument({ images: studentActivity?.images, documents: studentActivity?.files });

  const { handleUploadFile } = useFileUpload();

  const viewerImages: ImageDecorator[] = [];
  if (activity?.images) {
    for (const image of activity.images) {
      if (isPdfFile(image) == false) {
        viewerImages.push({
          src: `${Constants.imageUrl}${image}`,
        });
      }
    }
  }

  const disabled = !!(
    (activity.isFile &&
      activity.isFileRequired &&
      [...imageObjectMap.values()].length === 0 &&
      [...documentObjectMap.values()].length === 0) ||
    (activity.isContent && activity.isContentRequired && content === '')
  );

  // 활동 제출 처리 함수
  const handleActivitySubmit = async () => {
    // 설문 유형일 경우 필수 항목 검증
    if (activity?.type === 'SURVEY' && activity?.surveyContent) {
      try {
        const surveyContent = JSON.parse(activity.surveyContent);
        const contentJson = content ? JSON.parse(content) : {};

        // 필수 항목 검증
        for (const question of surveyContent) {
          if (question.required) {
            const value = contentJson[question.id];
            let isValid = false;

            switch (question.type) {
              case 'text':
              case 'longtext':
                isValid = !!value && value.trim() !== '';
                break;
              case 'checkbox':
                isValid = Array.isArray(value) && value.length > 0;
                break;
              case 'radiobutton':
              case 'image':
              case 'file':
                isValid = !!value;
                break;
              default:
                isValid = true;
            }

            if (!isValid) {
              setToastMsg(`${question.id}번 질문의 답변이 입력되지 않았습니다.`);
              return;
            }
          }
        }
      } catch (error) {
        console.error('설문 검증 중 오류 발생:', error);
        setToastMsg('설문 내용 검증 중 오류가 발생했습니다.');
        return;
      }
    }

    setLoading(true);

    try {
      // 이미지 파일 처리
      const imageFiles = [...imageObjectMap.values()]
        .filter((value) => !value.isDelete && value.image instanceof File)
        .map((value) => value.image) as File[];
      const imageFileNames = await handleUploadFile(UploadFileTypeEnum['activityv3/images'], imageFiles);

      // url 이미지 처리
      const imageUrlNames = [...imageObjectMap.values()]
        .filter((value) => !value.isDelete && typeof value.image === 'string')
        .map((value) => value.image) as string[];
      const allImageNames = [...imageUrlNames, ...imageFileNames];

      // 문서 파일 처리
      const documentFiles = [...documentObjectMap.values()]
        .filter((value) => !value.isDelete && value.document instanceof File)
        .map((value) => value.document) as File[];
      const documentFileNames = await handleUploadFile(UploadFileTypeEnum['activityv3/files'], documentFiles);

      // url 문서 처리
      const documentUrlNames = [...documentObjectMap.values()]
        .filter((value) => !value.isDelete && typeof value.document === 'string')
        .map((value) => value.document) as string[];
      const allDocumentNames = [...documentUrlNames, ...documentFileNames];

      // 활동 저장
      await saveStudentActivitySession({
        params: { sessionId: activity.id },
        data: { content, images: allImageNames, files: allDocumentNames },
      });
      refetch();
    } catch (error) {
      console.error('활동 제출 중 오류가 발생했습니다:', error);
      setToastMsg('활동 제출 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const { editableStatusText, isEditable, isLateSubmissionAvailable } = getSessionTimeInfo(activity);
  const checkEditStatus = async () => {
    if (!isEditable) {
      setToastMsg(editableStatusText);
      return false;
    } else if (isLateSubmissionAvailable) {
      if (
        !(await confirm(
          '지각제출 기간 알림',
          `현재 지각제출 기간입니다. 수정 시 '지각제출'로 표시됩니다.
학교 정책에 따라 감점이 될 수 있습니다.`,
          '취소',
          '확인',
        ))
      ) {
        return;
      }
    }
    return handleActivitySubmit();
  };

  const getSubmitContentElement = () => {
    switch (activity?.type) {
      case 'SURVEY':
        return (
          <>
            <SuperSurveyComponent
              surveyContent={activity?.surveyContent || '[]'}
              setContent={(c: any) => setContent(JSON.stringify(c))}
              content={content ? JSON.parse(content) : {}}
              readOnly={readOnly}
              disabled={!isEditable}
            />
            {studentActivity?.isSubmitted ? (
              <div className="mt-auto flex flex-row gap-2 border-t border-t-primary-gray-100 px-5 pt-5">
                <ButtonV2 variant="solid" color="gray100" size={48} className="h-14 w-full" onClick={() => refetch()}>
                  취소
                </ButtonV2>
                <ButtonV2
                  variant="solid"
                  color="orange100"
                  size={48}
                  className="h-14 w-full"
                  disabled={!isEditable || readOnly}
                  onClick={checkEditStatus}
                >
                  다시 제출하기
                </ButtonV2>
              </div>
            ) : (
              <div className="mt-auto border-t border-t-primary-gray-100 px-5 pt-5">
                <ButtonV2
                  variant="solid"
                  color="orange800"
                  size={48}
                  className="h-14 w-full"
                  disabled={!isEditable || readOnly}
                  onClick={checkEditStatus}
                >
                  {editableStatusText}
                </ButtonV2>
              </div>
            )}
          </>
        );
      case 'NOTICE':
        return <></>;
      // 과제 타입
      case 'POST':
        return (
          <div className="flex flex-col gap-6 px-5">
            {activity?.isFile && (
              <div className="flex flex-col gap-2">
                <Typography variant="title3" className="font-semibold">
                  첨부자료
                </Typography>
                <label htmlFor="file-upload" className="inline-block cursor-pointer">
                  <ButtonV2
                    variant="outline"
                    color="gray400"
                    size={40}
                    disabled={!isEditable}
                    className="flex items-center gap-1"
                    onClick={() => {
                      const fileInput = document.getElementById('file-upload') as HTMLInputElement;
                      if (fileInput) {
                        fileInput.click();
                      }
                    }}
                  >
                    <SVGIcon.Plus size={16} weight="bold" color={!isEditable ? 'gray400' : 'gray700'} />
                    <Typography variant="body2" className={`font-medium ${!isEditable ? 'text-primary-gray-400' : ''}`}>
                      파일첨부
                    </Typography>
                  </ButtonV2>
                  <input
                    type="file"
                    multiple
                    className="sr-only"
                    id="file-upload"
                    onChange={(e) => {
                      if (!e.target.files || e.target.files.length === 0) return;
                      if (!checkFileSizeLimit100MB([...e.target.files])) {
                        return alert('100MB가 넘는 파일은 첨부할 수 없습니다.');
                      }
                      addFiles(e.target.files, [fileType.ANY]);
                    }}
                  />
                </label>
                {([...imageObjectMap].length > 0 || [...documentObjectMap].length > 0) && (
                  <div className="flex flex-col gap-2">
                    <div className="flex flex-row gap-2 overflow-x-auto">
                      {[...imageObjectMap].map(([key, value]) => (
                        <ImageCard key={key} id={key} imageObjet={value} onDeleteClick={toggleImageDelete} />
                      ))}
                    </div>
                    {[...documentObjectMap].map(([key, value]) => (
                      <DocumentObjectComponentDel
                        key={key}
                        id={key}
                        documentObjet={value}
                        onDeleteClick={toggleDocumentDelete}
                      />
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-1">
                  <SolidSVGIcon.Info size={16} color="gray400" />
                  <Typography variant="caption" className="text-primary-gray-500">
                    100MB 이하 이미지/문서 파일, 다중 첨부 가능
                  </Typography>
                </div>
              </div>
            )}
            {activity?.isContent && (
              <section className="flex flex-col gap-2">
                <Typography variant="title3" className="font-semibold">
                  내용
                </Typography>
                <TextareaV2
                  disabled={readOnly || !isEditable}
                  placeholder="내용을 입력해주세요."
                  rows={5}
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  className="h-[480px]"
                  showContentCount
                />
              </section>
            )}
            {studentActivity?.isSubmitted ? (
              <nav className="flex flex-row items-center justify-between gap-2">
                <ButtonV2
                  variant="solid"
                  color="gray100"
                  size={40}
                  children={'취소'}
                  className="h-14 w-full"
                  onClick={() => {
                    refetch();
                  }}
                />
                <ButtonV2
                  variant="solid"
                  color="orange100"
                  size={40}
                  children={'다시 제출하기'}
                  className="h-14 w-full"
                  disabled={disabled || isLoading || readOnly}
                  onClick={checkEditStatus}
                />
              </nav>
            ) : (
              <ButtonV2
                variant="solid"
                color="orange800"
                size={40}
                children={editableStatusText}
                disabled={disabled || isLoading || readOnly || !isEditable}
                onClick={checkEditStatus}
              />
            )}
          </div>
        );
    }
  };

  return (
    <div>
      {getSubmitContentElement()}
      <br />
    </div>
  );
};
